'use client';

import React, { PointerEvent, useCallback } from 'react';

import type {
  Selection,
  HandleDirection
} from '../../ThreeDStockContentImageLensTypes';

export type { HandleDirection };
type Size = 's' | 'm' | 'l';

interface ThreeDStockContentImageLensResizeButtonProps {
  direction: HandleDirection;
  selection: Selection;
  onPointerDown: (direction: HandleDirection) => (e: PointerEvent) => void;
}

const HANDLE_SIZE: Record<Size, number> = { s: 8, m: 16, l: 32 };
const HANDLE_INNER_SIZE = 64;
const HANDLE_BORDER_WIDTH = 4;
const HANDLE_OFFSET = 2;

const HANDLE_LABELS: Record<HandleDirection, string> = {
  nw: 'Resize top left',
  ne: 'Resize top right',
  sw: 'Resize bottom left',
  se: 'Resize bottom right',
  n: 'Resize top',
  s: 'Resize bottom',
  e: 'Resize right',
  w: 'Resize left',
  move: 'Move selection'
};

const HANDLE_CURSOR: Record<HandleDirection, string> = {
  nw: 'cursor-nwse-resize',
  ne: 'cursor-nesw-resize',
  sw: 'cursor-nesw-resize',
  se: 'cursor-nwse-resize',
  n: 'cursor-ns-resize',
  s: 'cursor-ns-resize',
  e: 'cursor-ew-resize',
  w: 'cursor-ew-resize',
  move: 'cursor-move'
};

const SIZE_CLASS: Record<Size, string> = {
  s: 'h-2 w-2',
  m: 'h-4 w-4',
  l: 'h-8 w-8'
};

const EDGE_THICKNESS = 8;

const getSize = ({ w, h }: Pick<Selection, 'w' | 'h'>): Size => {
  if (w > 64 && h > 64) return 'l';
  if (w > 32 && h > 32) return 'm';
  return 's';
};

const getDirectionFlags = (direction: HandleDirection) => ({
  isTop: direction === 'nw' || direction === 'ne',
  isLeft: direction === 'nw' || direction === 'sw'
});

const getHandlePosition = (
  selection: Selection,
  direction: HandleDirection,
  size: Size
): React.CSSProperties => {
  const handleSize = HANDLE_SIZE[size];

  if (direction === 'n') {
    return {
      left: selection.x + selection.w / 2 - EDGE_THICKNESS / 2,
      top: selection.y - HANDLE_OFFSET,
      width: EDGE_THICKNESS,
      height: handleSize
    };
  }

  if (direction === 's') {
    return {
      left: selection.x + selection.w / 2 - EDGE_THICKNESS / 2,
      top: selection.y + selection.h - handleSize + HANDLE_OFFSET,
      width: EDGE_THICKNESS,
      height: handleSize
    };
  }

  if (direction === 'e') {
    return {
      left: selection.x + selection.w - handleSize + HANDLE_OFFSET,
      top: selection.y + selection.h / 2 - EDGE_THICKNESS / 2,
      width: handleSize,
      height: EDGE_THICKNESS
    };
  }

  if (direction === 'w') {
    return {
      left: selection.x - HANDLE_OFFSET,
      top: selection.y + selection.h / 2 - EDGE_THICKNESS / 2,
      width: handleSize,
      height: EDGE_THICKNESS
    };
  }

  if (direction === 'move') {
    const padding = handleSize;
    return {
      left: selection.x + padding,
      top: selection.y + padding,
      width: Math.max(0, selection.w - padding * 2),
      height: Math.max(0, selection.h - padding * 2)
    };
  }

  const { isTop, isLeft } = getDirectionFlags(direction);
  return {
    left: isLeft
      ? selection.x - HANDLE_OFFSET
      : selection.x + selection.w - handleSize + HANDLE_OFFSET,
    top: isTop
      ? selection.y - HANDLE_OFFSET
      : selection.y + selection.h - handleSize + HANDLE_OFFSET
  };
};

const getInnerOffset = (direction: HandleDirection, size: Size) => {
  if (
    direction === 'n' ||
    direction === 's' ||
    direction === 'e' ||
    direction === 'w' ||
    direction === 'move'
  ) {
    return { marginTop: 0, marginLeft: 0 };
  }
  const { isTop, isLeft } = getDirectionFlags(direction);
  const handleSize = HANDLE_SIZE[size];
  return {
    marginTop: isTop ? 0 : handleSize - HANDLE_INNER_SIZE,
    marginLeft: isLeft ? 0 : handleSize - HANDLE_INNER_SIZE
  };
};

function ThreeDStockContentImageLensResizeButton({
  direction,
  selection,
  onPointerDown
}: ThreeDStockContentImageLensResizeButtonProps) {
  const size = getSize(selection);
  const position = getHandlePosition(selection, direction, size);
  const innerOffset = getInnerOffset(direction, size);

  const handlePointerDown = useCallback(
    (e: PointerEvent) => {
      onPointerDown(direction)(e);
    },
    [direction, onPointerDown]
  );

  const isEdgeOrMove =
    direction === 'n' ||
    direction === 's' ||
    direction === 'e' ||
    direction === 'w' ||
    direction === 'move';

  return (
    <button
      type="button"
      aria-label={HANDLE_LABELS[direction]}
      data-lens-handle="true"
      className={`absolute ${
        isEdgeOrMove ? '' : SIZE_CLASS[size]
      } overflow-hidden ${HANDLE_CURSOR[direction]}`}
      style={position}
      onPointerDown={handlePointerDown}
    >
      {!isEdgeOrMove && (
        <div
          className="rounded-2xl border-white"
          style={{
            ...innerOffset,
            width: HANDLE_INNER_SIZE,
            height: HANDLE_INNER_SIZE,
            borderWidth: HANDLE_BORDER_WIDTH
          }}
        />
      )}
    </button>
  );
}

export default ThreeDStockContentImageLensResizeButton;
